// Hybrid encryption (ARCHITECTURE.md §4).
//
// The profile is encrypted ONCE with a random content key K (AES-256-GCM).
// K is then wrapped separately for each recipient: a static-static ECDH
// between the author's encryption key and the recipient's encryption public
// key yields a shared secret, HKDF-expanded into an AES key that encrypts K.
//
// This decouples per-recipient cost from payload size (photos later) and keeps
// every operation inside WebCrypto with non-extractable private keys.

import { importEcdhPublic } from './keys';

const subtle = globalThis.crypto.subtle;
const WRAP_SALT = new TextEncoder().encode('circle/v1/wrap');
const WRAP_INFO = new TextEncoder().encode('circle/v1/keywrap/p256');

/** AES-GCM ciphertext with its IV. */
export interface Sealed {
  iv: Uint8Array;
  ciphertext: Uint8Array;
}

function randomBytes(len: number): Uint8Array {
  return globalThis.crypto.getRandomValues(new Uint8Array(len));
}

/** Fresh random 256-bit content key (raw bytes). */
export function generateContentKey(): Uint8Array {
  return randomBytes(32);
}

async function aesKey(raw: Uint8Array, usages: KeyUsage[]): Promise<CryptoKey> {
  return subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, usages);
}

/** Encrypt an arbitrary payload with the content key. */
export async function sealContent(contentKey: Uint8Array, plaintext: Uint8Array): Promise<Sealed> {
  const iv = randomBytes(12);
  const key = await aesKey(contentKey, ['encrypt']);
  const ct = await subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  return { iv, ciphertext: new Uint8Array(ct) };
}

/** Decrypt a payload with the content key. */
export async function openContent(contentKey: Uint8Array, sealed: Sealed): Promise<Uint8Array> {
  const key = await aesKey(contentKey, ['decrypt']);
  const pt = await subtle.decrypt({ name: 'AES-GCM', iv: sealed.iv }, key, sealed.ciphertext);
  return new Uint8Array(pt);
}

/** Derive the per-pair wrapping key from a static-static ECDH shared secret. */
async function wrapKeyFor(
  myEcdhPrivate: CryptoKey,
  theirEcdhPublicRaw: Uint8Array,
  usages: KeyUsage[],
): Promise<CryptoKey> {
  const theirPublic = await importEcdhPublic(theirEcdhPublicRaw);
  const shared = await subtle.deriveBits({ name: 'ECDH', public: theirPublic }, myEcdhPrivate, 256);
  const base = await subtle.importKey('raw', shared, 'HKDF', false, ['deriveKey']);
  return subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: WRAP_SALT, info: WRAP_INFO },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    usages,
  );
}

/** Wrap the content key for a single recipient. */
export async function wrapContentKey(
  authorEcdhPrivate: CryptoKey,
  recipientEcdhPublicRaw: Uint8Array,
  contentKey: Uint8Array,
): Promise<Sealed> {
  const key = await wrapKeyFor(authorEcdhPrivate, recipientEcdhPublicRaw, ['encrypt']);
  const iv = randomBytes(12);
  const ct = await subtle.encrypt({ name: 'AES-GCM', iv }, key, contentKey);
  return { iv, ciphertext: new Uint8Array(ct) };
}

/** Recover the content key wrapped for me by a known author. */
export async function unwrapContentKey(
  myEcdhPrivate: CryptoKey,
  authorEcdhPublicRaw: Uint8Array,
  wrapped: Sealed,
): Promise<Uint8Array> {
  const key = await wrapKeyFor(myEcdhPrivate, authorEcdhPublicRaw, ['decrypt']);
  const raw = await subtle.decrypt({ name: 'AES-GCM', iv: wrapped.iv }, key, wrapped.ciphertext);
  return new Uint8Array(raw);
}
